/**
 * Contact page copy, plus the service options shown in the enquiry dropdown.
 * The values here must stay in sync with the enum in shared/schemas.ts.
 */

export const contact = {
  hero: {
    eyebrow: 'Contact',
    headline: 'Tell us what you are building',
    subcopy:
      'Talk to a software development team in Calgary about your web app, mobile app or automation project. Send a short description of the problem and you will get honest thoughts on approach, scope and whether we are the right fit.',
  },

  form: {
    heading: 'Send a message',
    /** Values must match contactSchema's `service` enum. */
    serviceOptions: [
      { value: 'software', label: 'Software & Web Development' },
      { value: 'mobile', label: 'Mobile App Development' },
      { value: 'ai-data', label: 'AI & Data Solutions' },
      { value: 'ai-automation', label: 'AI Automation' },
      { value: 'qa-automation', label: 'QA Automation' },
      { value: 'workflow', label: 'Workflow Automation' },
      { value: 'other', label: 'Something else' },
    ],
    successHeading: 'Message received',
    successBody: 'Thanks, we have got it. Expect a reply from someone technical, usually within one working day.',
  },

  details: {
    heading: 'Other ways to reach us',
    responsePromise: 'We reply to every enquiry within one working day.',
    note: 'For job applications, please use the careers page so your details reach the right inbox.',
  },

  map: {
    eyebrow: 'Where we are',
    heading: 'Calgary, with a second team in Lahore',
    body: 'Our base is Calgary, Alberta, so clients across Canada and the US work with us in their own hours. A second development team in Lahore extends the day at the other end. Most projects run remotely; if you are in Calgary and would rather talk in person, say so and we will arrange it.',
  },

  /*
    Questions are phrased the way people actually search — "how much does
    custom software development cost", "do you work with existing codebases" —
    because this block is also emitted as FAQPage structured data
    (components/seo/StructuredData.tsx) and can surface directly in results.
    Each answer opens with a direct response rather than a preamble, since a
    rich result may show only the first line or two.
  */
  faq: {
    eyebrow: 'Before you ask',
    heading: 'Software development FAQs',
    items: [
      {
        q: 'How much does custom software development cost?',
        a: 'Our pricing page compares possible scopes for AI applications, MVPs and commercial software. Each project receives a written quote confirming deliverables, timeline, currency, support and applicable third-party costs before work begins. Changes to the agreed scope are discussed and approved separately.',
      },
      {
        q: 'How long does it take to build a web or mobile app?',
        a: 'A focused tool or automation usually takes a few weeks. A full web or mobile application with authentication, payments and an admin area is typically a few months. The scoping document gives you a phased timeline, and the first usable version lands deliberately early so you can react to something real.',
      },
      {
        q: 'Do you work with existing codebases and legacy software?',
        a: 'Yes, and a good deal of our work is exactly that: adding features to something already running, fixing what has become fragile, or restarting a stalled project. We begin with a short audit of the existing code so we can be honest about its condition before quoting anything.',
      },
      {
        q: 'Do you offer software maintenance and support after launch?',
        a: 'Yes, and it is your choice which way to go. We can hand over completely with documentation and a maintenance runbook so your own team takes it on, or stay on for ongoing support and development. There is no lock-in either way, and the code is yours from the start.',
      },
      {
        q: 'Do you work with clients outside Calgary?',
        a: 'Yes. We are based in Calgary, Alberta and work with clients across Canada and the United States, with most projects running remotely. A second development team in Lahore extends the working day at the other end. If you are in Calgary and would rather meet in person, that is easily arranged.',
      },
      {
        q: 'What does AI automation actually involve?',
        a: 'Usually it means connecting a language model to your own documents and records so it can classify, extract or draft against real data rather than guess. We build these with a confidence threshold and a human review path, plus an audit log of every automated decision, so nothing important is decided silently.',
      },
      {
        q: 'Do you sign NDAs?',
        a: 'Yes, routinely, and we are happy to sign yours before you describe the project in any detail.',
      },
    ],
  },
} as const;
